/**
 * Работа с Document Object Model (DOM)
 * @packageDocumentation
 */

/**
 * Создать элемент
 * @param tagName
 * @param attrs - атрибуты
 * @param elsChildrens
 * @returns - созданный элемент
 */
export function genEl(tagName: string, attrs?: Record<string, string>, ...elsChildrens: HTMLElement[] | string[]): HTMLElement {
	const el: any = document.createElement(tagName);

	for (const name in attrs) {
		if (name.indexOf('on') === 0) {
			// events
			el[name] = attrs[name];
		} else {
			el.setAttribute(name, attrs[name]);
		}
	}

	elsChildrens.forEach((elChildren: HTMLElement | string) => {
		if (typeof (elChildren) === 'string') {
			el.insertAdjacentHTML('beforeend', elChildren);
		} else {
			el.append(elChildren);
		}
	});

	return el;
}

/**
 * Проверить видимость элемента
 * @param el
 * @returns - вернет true, если элемент считается видимым
 */
export function isVisible(el?: HTMLElement): boolean {
	if (!el) {
		return false;
	}

	const boundingClientRect = el.getBoundingClientRect();
	return !!(boundingClientRect.width || boundingClientRect.height);
}

/**
 * Получить координаты элемента относительно документа
 * @param el
 * @returns - координаты элемента относительно документа
 */
export function offset(el: HTMLElement): { top: number, left: number } {
	const boundingClientRect = el.getBoundingClientRect();

	return {
		top: boundingClientRect.top + window.scrollY,
		left: boundingClientRect.left + window.scrollX,
	};
}

/**
 * Получить значение css свойства элемента
 * @param el
 * @param property - имя свойства
 * @return - значение css стиля
 */
export function css(el: HTMLElement, property: string): string {
	return window.getComputedStyle(el).getPropertyValue(property);
}

/**
 * Обернуть элемент и вернуть только что созданный элемент
 * @param el - элемент, который требуется обернуть
 * @param tagName - имя тега элемента, в который треубется обернуть элемент
 */
export function wrap(el: HTMLElement, tagName: string): HTMLElement {
	const elWrapper = document.createElement(tagName);
	el.after(elWrapper);

	elWrapper.append(el);

	return elWrapper;
}

/**
 * Найти и вернуть первый видимый элемент
 * @param el - корневой элемент поиска
 * @param selectors - см. параметр selectors в Document.querySelectorAll()
 */
export function querySelectorVisible(el: HTMLElement, selectors: string): HTMLElement | null {
	return querySelectorAllArray(el, selectors).find(isVisible) || null;
}

/**
 * Найти и вернуть последний видимый элемент
 * @param el - корневой элемент поиска
 * @param selectors - см. параметр selectors в Document.querySelectorAll()
 */
export function querySelectorVisibleLast(el: HTMLElement, selectors: string): HTMLElement | null {
	return querySelectorAllArray(el, selectors).findLast(isVisible) || null;
}

/**
 * Найти видимые элементы
 * @param el - корневой элемент поиска
 * @param selectors - см. параметр selectors в Document.querySelectorAll()
 */
export function querySelectorAllVisible(el: HTMLElement | Document, selectors: string): HTMLElement[] {
	return querySelectorAllArray(el, selectors).filter(isVisible);
}

/**
 * Найти элементы и вернуть в виде массива
 * @param el - корневой элемент поиска
 * @param selectors - см. параметр selectors в Document.querySelectorAll()
 */
export function querySelectorAllArray(el: HTMLElement | Document, selectors: string): HTMLElement[] {
	if (!el) {
		return [];
	}

	return [...el.querySelectorAll(selectors)] as HTMLElement[];
}

/**
 * Хранилище данных элемента
 * @param el
 * @param contextName - контекст, обычно используется имя компонента
 * @param contextData - установить объект с данными, предыдущие данные будут заменены, установите null для удаления данных
 * @returns - вернет объект контекста или undefined, если данные контекста не найдены
 */
export function storage(
	el?: HTMLElement & { topData?: any },
	contextName: string = 'common',
	contextData: object | null | undefined = undefined,
): any | undefined {
	if (!el) {
		return;
	}

	if (!el.topData) {
		el.topData = {};
	}

	if (contextData) {
		el.topData[contextName] = contextData;
	}

	if (contextData === null) {
		delete el.topData[contextName];
	}

	return el.topData[contextName];
}

/**
 * Очистить хранилище элемента
 */
export function storageClear(el?: HTMLElement & { topData?: any }): void {
	if (!el) {
		return;
	}

	delete el.topData;
}

export default {
	genEl,
	isVisible,
	offset,
	css,
	wrap,
	querySelectorVisible,
	querySelectorVisibleLast,
	querySelectorAllVisible,
	querySelectorAllArray,
	storage,
	storageClear,
};
